import { ServerResponse } from "http"
import { WithRouterProps } from "next/dist/client/with-router"
import Error from "next/dist/pages/_error"
import Head from "next/head"
import { withRouter } from "next/router"
import React from "react"
import styled, { css } from "styled-components"

import Pc from "~/components/atoms/Pc"
import Sp from "~/components/atoms/Sp"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Pagination from "~/components/molecules/Pagination"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import { Color, TextSize, IsPC, IsMobile } from "~/consts/style-mixins"
import { PageInfo } from "~/entities/PageInfo"
import { ShortInternship } from "~/entities/ShortInternship"
import RecruitmentList from "~/observers/RecruitmentList"
import TabBar from "~/observers/TabBar"
import { canonicalUrl, internalLink, staticPath } from "~/services/helper"
import RootStore from "~/stores/RootStore"

interface Props {
  shortInternships: ShortInternship[]
  pageInfo: PageInfo
  pathname: string
}

class ShortInternships extends React.Component<Props & WithRouterProps> {
  static async getInitialProps({
    initialStoreData,
    pathname,
    query,
    res,
  }: {
    initialStoreData: RootStore
    pathname: string
    query: any
    res: ServerResponse
  }) {
    const { shortInternshipStore } = initialStoreData
    const { shortInternships, pageInfo } = await shortInternshipStore.fetch({
      page: query.page || 1,
    })
    // NOTE: 存在しないページ数が指定された際は 404 を返す
    const { currentPage, lastPage } = pageInfo as PageInfo
    if (currentPage !== 1 && currentPage > lastPage) {
      res.statusCode = 404
    }
    return { shortInternships, pageInfo, pathname }
  }

  render() {
    const { shortInternships, pageInfo, pathname, router } = this.props
    const crumbs = [
      {
        name: "短期インターンシップ一覧",
        link: internalLink(`/short-internships`),
      },
    ]
    const yearFor = String(new Date().getFullYear() + 2).slice(-2)

    const { currentPage, lastPage } = pageInfo
    if (currentPage !== 1 && currentPage > lastPage) {
      return <Error statusCode={404} />
    }

    return (
      <>
        <Head>
          <title>短期インターンシップ募集一覧（{yearFor}卒向け） | Infra（インフラ）</title>
          <meta
            name="description"
            content={`大学生（主に${yearFor}卒）向けに Infra インターンが厳選した短期インターンシップの募集一覧ページです。大学生の方は是非このページで短期インターンを探してみましょう！`}
          />
          <meta property="og:title" content="短期インターンシップ募集一覧 | Infra（インフラ）" />
          <meta
            property="og:description"
            content={`大学生（主に${yearFor}卒）向けに Infra インターンが厳選した短期インターンシップの募集一覧ページです。大学生の方は是非このページで短期インターンを探してみましょう！`}
          />
          <meta property="og:image" content={`${process.env.APP_HOST + staticPath("/static/images/ogp.png")}`} />
          <link key="canonical" rel="canonical" href={canonicalUrl(router.pathname, router.query)} />
        </Head>
        <Header showTab logoType="ShortIntern" />
        <Container size="Large">
          <StyledInner>
            <Pc>
              <StyledTypography type="SubHero">
                <h1>短期インターンシップ募集一覧</h1>
              </StyledTypography>
            </Pc>
            <Sp>
              <StyledTypography type="SubHero">
                <h1>短期インターンシップ募集一覧</h1>
              </StyledTypography>
            </Sp>
            <StyledRecruitmentNum>全{pageInfo.total}件の募集</StyledRecruitmentNum>
            <StyledListItemsWrapper>
              <StyledRecruitmentList type="shortInternship" recruitments={shortInternships} />
            </StyledListItemsWrapper>
            <Pagination
              baseUrl={pathname}
              queryStrings=""
              currentPage={pageInfo.currentPage}
              maxPage={pageInfo.lastPage}
              maxCount={pageInfo.total}
              currentMin={pageInfo.from}
              currentMax={pageInfo.to}
            />
          </StyledInner>
        </Container>
        <Breadcrumb crumbs={crumbs} />
        <Sp>
          <TabBar selectedTabIndex={0} />
        </Sp>
        <Footer />
      </>
    )
  }
}

const StyledTypography = styled(Typography)`
  margin-bottom: 8px;
`
const StyledInner = styled.div`
  ${IsPC(css`
    padding: 48px 0;
    min-height: calc(100vh - 243px);
  `)}
  ${IsMobile(css`
    padding: 24px 0;
    min-height: calc(100vh - 358px);
  `)}
`

const StyledRecruitmentNum = styled.div`
  margin-bottom: 24px;
  font-size: ${TextSize.S}px;
  color: ${Color.LightGray};
`

const StyledListItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${IsPC(css`
    margin: 0 -8px;
  `)}
`

const StyledRecruitmentList = styled(RecruitmentList)`
  margin-bottom: 24px;
  ${IsPC(css`
    width: 33.3%;
    padding: 0 8px;
  `)}
`
export default withRouter(ShortInternships)
